import { Component, OnInit, computed, inject, signal } from '@angular/core';
import {
  OrderService,
  OrderDetails,
  OrderBasketItem,
  OrderBasketDetails,
  BasketItemType,
  CartV2Service,
  CheckoutService,
  MarketplaceSubType,
  MarketplaceFacade,
} from '@fgb/core';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'fgb-unified-checkout-success-page',
  templateUrl: './unified-checkout-success-page.component.html',
  styleUrl: './unified-checkout-success-page.component.scss',
})
export class UnifiedCheckoutSuccessPageComponent implements OnInit {
  /** The order, once fetched. Null until the response lands. */
  readonly orderDetails = signal<OrderDetails | null>(null);

  /** The order's baskets, with each basket's duplicate lines merged into one. */
  readonly basketItems = computed<OrderBasketDetails[]>(() => {
    const order = this.orderDetails();
    if (!order) {
      return [];
    }

    return order.Basket.map((basket) => ({ ...basket, Items: this.mapUniquePurchases(basket.Items) }));
  });

  /** The first item of the first basket, which drives the page's heading. */
  readonly firstBasketItem = computed<OrderBasketItem | null>(() => this.basketItems()[0]?.Items[0] ?? null);
  readonly totalItemCount = computed(() => this.basketItems().reduce((count, basket) => count + basket.Items.length, 0));

  basketItemType = BasketItemType;
  marketplaceSubType = MarketplaceSubType;

  private readonly orderService = inject(OrderService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly cartService = inject(CartV2Service);
  private readonly checkoutService = inject(CheckoutService);
  private readonly marketplaceFacade = inject(MarketplaceFacade);

  ngOnInit() {
    const orderId = this.readOrderId();

    this.orderService.getOrderDetails(orderId).subscribe((order) => {
      this.orderDetails.set(order);
      this.marketplaceFacade.clearCache();
    });

    if (this.checkoutService.isCartCheckout()) {
      this.cartService.removeAll();
    }

    this.checkoutService.clearAll();
  }

  /**
   * The order id from the url, whatever case it arrives in.
   *
   * queryParamMap.get() is an exact match, and this page is reached two ways:
   * the app navigates to it with `?orderId=`, while a payment provider
   * redirects back with `?OrderId=`. Reading either alone loses the other, so
   * the key is matched case-insensitively.
   */
  private readOrderId(): string {
    const params = this.activatedRoute.snapshot.queryParamMap;
    const key = params.keys.find((candidate) => candidate.toLowerCase() === 'orderid');

    return (key ? params.get(key) : null) ?? '';
  }

  private mapUniquePurchases(items: OrderBasketItem[]): OrderBasketItem[] {
    let uniquePurchases: Map<number, OrderBasketItem> = new Map();
    items.forEach((item) => {
      const existing = uniquePurchases.get(item.Id);
      if (existing) {
        uniquePurchases.set(item.Id, {
          ...existing,
          Quantity: existing.Quantity + item.Quantity,
          Merchant: existing.Merchant + item.Merchant,
          Ecash: existing.Ecash + item.Ecash,
          Points: existing.Points + item.Points,
        });
      } else {
        uniquePurchases.set(item.Id, { ...item });
      }
    });
    return Array.from(uniquePurchases.values());
  }
}
